/**
 * Expresiones MapLibre a partir del contrato de filtros y el estado activo.
 *
 * Null-safety: las props del tile pueden faltar o venir null en parte de las
 * parcelas (v8 no embebe todo en todas). Reglas:
 * - Strings: `downcase` lanza con null → siempre pasar por `to-string` (null → "").
 * - Números: `to-number(null)` es 0 → exigir `has` para no matchear 0 espurios.
 */

import { getParcelFilterDefinitions } from './parcelFilterContract';

function numericFieldExpr(tileField) {
  return ['to-number', ['get', tileField]];
}

/** `to-string` convierte null en "" y evita el type error de `downcase`. */
function stringFieldExpr(tileField) {
  return ['downcase', ['to-string', ['get', tileField]]];
}

/** Condición que además exige que la prop exista en la feature. */
function withHas(tileField, condition) {
  return ['all', ['has', tileField], condition];
}

function fieldIsPresent(tileField) {
  return ['all', ['has', tileField], ['!=', ['get', tileField], null]];
}

/**
 * Número del tile. Si hay `tileFieldFallback` (superficie: urbansurface → surface),
 * usa el primario cuando está y no es null.
 * @param {object} def
 * @returns {object[]}
 */
function numericSourceExpr(def) {
  const tileField = def.tileField || def.field;
  const fallback = def.tileFieldFallback;
  if (!fallback || fallback === tileField) {
    return numericFieldExpr(tileField);
  }
  return ['to-number', [
    'case',
    fieldIsPresent(tileField),
    ['get', tileField],
    ['get', fallback],
  ]];
}

function withHasNumericSource(def, condition) {
  const tileField = def.tileField || def.field;
  const fallback = def.tileFieldFallback;
  if (!fallback || fallback === tileField) {
    return withHas(tileField, condition);
  }
  return ['all',
    ['any', fieldIsPresent(tileField), fieldIsPresent(fallback)],
    condition,
  ];
}

/**
 * Variantes aceptadas para un valor de multiSelect.
 * `matchValues` de la opción permite matchear un mismo valor escrito distinto en
 * el tile (por ejemplo "Córdoba" y "CORDOBA" para la provincia).
 */
function getMatchValues(def, value) {
  const option = (def.options || []).find((opt) => opt.value === value);
  const variants = option?.matchValues?.length ? option.matchValues : [value];
  return Array.from(new Set(variants.map((v) => String(v).toLowerCase())));
}

function buildMultiSelectCondition(def, values) {
  const tileField = def.tileField || def.field;
  const isNumber = def.valueType === 'number';
  const fieldExpr = isNumber ? numericFieldExpr(tileField) : stringFieldExpr(tileField);

  const parts = isNumber
    ? values.map((value) => ['==', fieldExpr, value])
    : values
      .flatMap((value) => getMatchValues(def, value))
      .map((variant) => ['==', fieldExpr, variant]);

  const anyMatch = parts.length === 1 ? parts[0] : ['any', ...parts];
  return isNumber ? withHas(tileField, anyMatch) : anyMatch;
}

function buildDualFastCondition(tileField, values) {
  const field = numericFieldExpr(tileField);
  const parts = values.map((v) => ['==', field, v]);
  const anyMatch = parts.length === 1 ? parts[0] : ['any', ...parts];
  return withHas(tileField, anyMatch);
}

/**
 * Bandas sobre campo continuo: cada opción aporta min/max.
 * Bandas bucketizadas (`matchMode: 'exact'`): igualdad contra el valor del tile.
 */
function buildRangeBandsCondition(def, selectedValues) {
  const tileField = def.tileField || def.field;
  const selected = def.options.filter((opt) => selectedValues.includes(opt.value));
  if (selected.length === 0) return null;

  if (def.matchMode === 'exact') {
    return buildDualFastCondition(tileField, selected.map((opt) => opt.value));
  }

  const field = numericSourceExpr(def);
  const bandExprs = selected.map((opt) => {
    const parts = [];
    if (opt.min != null) parts.push(['>=', field, opt.min]);
    if (opt.max != null) {
      parts.push([opt.maxExclusive ? '<' : '<=', field, opt.max]);
    }
    if (parts.length === 0) return null;
    return parts.length === 1 ? parts[0] : ['all', ...parts];
  }).filter(Boolean);

  if (bandExprs.length === 0) return null;
  const anyMatch = bandExprs.length === 1 ? bandExprs[0] : ['any', ...bandExprs];
  return withHasNumericSource(def, anyMatch);
}

/** Quita diacríticos. MapLibre no puede hacerlo dentro de una expresión. */
function deburrText(value) {
  return String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/** Normaliza el texto tipeado: sin extremos, espacios colapsados, minúsculas. */
export function normalizeTextQuery(query, { stripAccents = false } = {}) {
  const normalized = String(query ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
  return stripAccents ? deburrText(normalized) : normalized;
}

function buildContainsExpr(tileField, needle) {
  return withHas(tileField, ['>=', ['index-of', needle, stringFieldExpr(tileField)], 0]);
}

/**
 * "Contiene" sobre un string del tile.
 * Con `normalizeQuery`, la consulta se desacentúa para comparar contra
 * `tileField` (p. ej. `street_name_norm`). `tileFieldFallback` cubre tilesets
 * que todavía no traen el campo normalizado.
 */
function buildTextContainsCondition(def, query) {
  const tileField = def.tileField || def.field;
  const fallbackField = def.tileFieldFallback;
  const needle = normalizeTextQuery(query);
  if (needle.length === 0) return null;

  const primaryNeedle = def.normalizeQuery ? normalizeTextQuery(query, { stripAccents: true }) : needle;
  const primary = buildContainsExpr(tileField, primaryNeedle);
  if (!fallbackField || fallbackField === tileField) return primary;

  return [
    'any',
    primary,
    ['all',
      ['!', ['has', tileField]],
      ['has', fallbackField],
      ['>=', ['index-of', needle, stringFieldExpr(fallbackField)], 0],
    ],
  ];
}

/**
 * @param {Record<string, unknown>} filters
 * @returns {object[]|null} expresión MapLibre filter
 */
export function buildParcelFilterMatchExpression(filters) {
  const conditions = [];

  getParcelFilterDefinitions().forEach((def) => {
    const value = filters[def.field];
    if (value == null) return;

    switch (def.component) {
      case 'multiSelect': {
        if (!Array.isArray(value) || value.length === 0) return;
        conditions.push(buildMultiSelectCondition(def, value));
        break;
      }
      case 'rangeBands': {
        if (!Array.isArray(value) || value.length === 0) return;
        const expr = buildRangeBandsCondition(def, value);
        if (expr) conditions.push(expr);
        break;
      }
      case 'triState': {
        if (value === 0 || value === 1) {
          conditions.push(withHas(def.field, ['==', numericFieldExpr(def.field), value]));
        }
        break;
      }
      case 'inlineBoolean': {
        if (value !== def.trueValue) break;
        const tileField = def.tileField || def.field;
        if (def.compare?.op != null && def.compare?.value != null) {
          conditions.push(withHas(
            tileField,
            [def.compare.op, numericFieldExpr(tileField), def.compare.value],
          ));
        } else {
          conditions.push(withHas(
            tileField,
            ['==', numericFieldExpr(tileField), def.trueValue],
          ));
        }
        break;
      }
      case 'textContains': {
        if (typeof value !== 'string') return;
        const expr = buildTextContainsCondition(def, value);
        if (expr) conditions.push(expr);
        break;
      }
      default:
        break;
    }
  });

  if (conditions.length === 0) return null;
  if (conditions.length === 1) return conditions[0];
  return ['all', ...conditions];
}

function hasTileProp(properties, field) {
  if (!properties || field == null) return false;
  const value = properties[field];
  return value != null && value !== '';
}

function readTileNumber(properties, field) {
  if (!hasTileProp(properties, field)) return null;
  const numeric = Number(properties[field]);
  return Number.isFinite(numeric) ? numeric : null;
}

function readTileNumberFromDef(properties, def) {
  const tileField = def.tileField || def.field;
  const primary = readTileNumber(properties, tileField);
  if (primary != null) return primary;
  if (def.tileFieldFallback) return readTileNumber(properties, def.tileFieldFallback);
  return null;
}

function compareNumber(left, op, right) {
  if (op === '>') return left > right;
  if (op === '>=') return left >= right;
  if (op === '<') return left < right;
  if (op === '<=') return left <= right;
  return left === right;
}

function matchesTileMultiSelect(def, properties, values) {
  const tileField = def.tileField || def.field;
  if (def.valueType === 'number') {
    const numeric = readTileNumber(properties, tileField);
    return numeric != null && values.some((value) => Number(value) === numeric);
  }
  const text = String(properties?.[tileField] ?? '').toLowerCase();
  return values.some((value) => getMatchValues(def, value).includes(text));
}

function matchesTileRangeBands(def, properties, selectedValues) {
  const selected = (def.options || []).filter((opt) => selectedValues.includes(opt.value));
  if (selected.length === 0) return false;

  const numeric = readTileNumberFromDef(properties, def);
  if (numeric == null) return false;

  if (def.matchMode === 'exact') {
    return selected.some((opt) => Number(opt.value) === numeric);
  }

  return selected.some((opt) => {
    if (opt.min != null && numeric < opt.min) return false;
    if (opt.max != null) {
      if (opt.maxExclusive ? numeric >= opt.max : numeric > opt.max) return false;
    }
    return opt.min != null || opt.max != null;
  });
}

function matchesTileTextContains(def, properties, query) {
  const tileField = def.tileField || def.field;
  const fallbackField = def.tileFieldFallback;
  const needle = normalizeTextQuery(query);
  if (needle.length === 0) return true;

  const primaryNeedle = def.normalizeQuery
    ? normalizeTextQuery(query, { stripAccents: true })
    : needle;

  if (hasTileProp(properties, tileField)) {
    const haystack = String(properties[tileField] ?? '').toLowerCase();
    const comparable = def.normalizeQuery ? deburrText(haystack) : haystack;
    return comparable.includes(primaryNeedle);
  }

  if (!fallbackField || fallbackField === tileField || !hasTileProp(properties, fallbackField)) {
    return false;
  }
  return String(properties[fallbackField] ?? '').toLowerCase().includes(needle);
}

function matchesTileFilter(def, properties, value) {
  switch (def.component) {
    case 'multiSelect':
      return Array.isArray(value) && value.length > 0
        ? matchesTileMultiSelect(def, properties, value)
        : true;
    case 'rangeBands':
      return Array.isArray(value) && value.length > 0
        ? matchesTileRangeBands(def, properties, value)
        : true;
    case 'triState': {
      if (value !== 0 && value !== 1) return true;
      const numeric = readTileNumber(properties, def.field);
      return numeric != null && numeric === value;
    }
    case 'inlineBoolean': {
      if (value !== def.trueValue) return true;
      const tileField = def.tileField || def.field;
      const numeric = readTileNumber(properties, tileField);
      if (numeric == null) return false;
      if (def.compare?.op != null && def.compare?.value != null) {
        return compareNumber(numeric, def.compare.op, def.compare.value);
      }
      return numeric === def.trueValue;
    }
    case 'textContains':
      return typeof value === 'string'
        ? matchesTileTextContains(def, properties, value)
        : true;
    default:
      return true;
  }
}

/**
 * Espejo en JS de `buildParcelFilterMatchExpression`: ¿las props del tile
 * cumplen los filtros de parcela? No evalúa listing ni UF oficiales.
 *
 * @param {Record<string, unknown>|null|undefined} properties
 * @param {Record<string, unknown>|null|undefined} filters
 * @returns {boolean}
 */
export function matchesParcelTileFilters(properties, filters) {
  if (!filters) return true;

  return getParcelFilterDefinitions().every((def) => (
    matchesTileFilter(def, properties || {}, filters[def.field])
  ));
}
